import { useState, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card"; 
import { CardDetails } from "../components/ui/details";
import Header from "../components/ui/header";
import axios from "axios";
import '../design/leads.css';
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import Footer from "../components/ui/footer";

export default function PropertyLeads() {
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [filters, setFilters] = useState({ 
    address: "", state: "", county: "", city: "", industry: "", status: "", saleDate: ""
  });
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isDetailsOpen, setDetailsOpen] = useState(false);
  const activeFilters = Object.keys(filters).filter(key => filters[key]);
  const [notes, setNotes] = useState(""); 
  const [noteSavedMessage, setNoteSavedMessage] = useState(""); 
  const [noteErrorMessage, setNoteErrorMessage] = useState("");
  const default_tags = ["Contacted", "Closed", "Follow Up", "Ignore", "Priority"]; 
  const [selectedTags, setSelectedTags] = useState([]);
  const [customTag, setCustomTag] = useState("");
  const [, setSaved] = useState(false);
  const token = localStorage.getItem("access_token");
  const [phone_number, setPhone] = useState(""); 
  const [email, setEmail] = useState("");  
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {

    if (token) {
      axios
        .get("https://api.deliquentauction.com/properties/", {
          headers: {
            Authorization: `Bearer ${token}`, 
          },
        })
        .then((response) => {
          setProperties(response.data);
          setFilteredProperties(response.data);
        })
        .catch((error) => console.error("Error fetching properties:", error));
    } else {
      console.log("No access token found.");
    }
  }, [token]);

  useEffect(() => {
    let filtered = [...properties];
    if (filters.address) filtered = filtered.filter((p) => p.full_address === filters.address);
    if (filters.city) filtered = filtered.filter((p) => p.parcel_city === filters.city);
    if (filters.state) filtered = filtered.filter((p) => p.state === filters.state);
    if (filters.county) filtered = filtered.filter((p) => p.county === filters.county);
    if (filters.industry) filtered = filtered.filter((p) => p.industry === filters.industry);
    if (filters.status) filtered = filtered.filter((p) => p.status === filters.status);
    if (filters.saleDate) {
      filtered = filtered.filter((p) => {
        const propertyDate = new Date(p.sale_date).toISOString().split("T")[0];  
        const selectedDate = new Date(filters.saleDate).toISOString().split("T")[0];  
        return propertyDate === selectedDate;
      });
    }

    filtered.sort((a, b) => a.full_address.localeCompare(b.full_address));
  
    setFilteredProperties(filtered);
  }, [filters, properties]);

  useEffect(() => {
    if (selectedProperty) {
      setPhone(selectedProperty.phone_number || "");
      setEmail(selectedProperty.email || "");
    }
  }, [selectedProperty]);  

  const removeFilter = (key) => {
    setFilters(prev => ({ ...prev, [key]: "" }));
  };

  const clearAllFilters = () => {
    setFilters({
      address: "", state: "", county: "", city: "", industry: "", status: "", saleDate: ""
    });
    setFilteredProperties(properties);
  };  

  const getFilteredOptions = (key) => {
    let filtered = properties;
  
    if (filters.address && key !== "address") {
      filtered = filtered.filter((p) => p.full_address === filters.address);
    }
    if (filters.city && key !== "city") {
      filtered = filtered.filter((p) => p.parcel_city === filters.city);
    }
    if (filters.state && key !== "state") {
      filtered = filtered.filter((p) => p.state === filters.state);
    }
    if (filters.county && key !== "county") {
      filtered = filtered.filter((p) => p.county === filters.county);
    }
    if (filters.industry && key !== "industry") {
      filtered = filtered.filter((p) => p.industry === filters.industry);
    }
    if (filters.status && key !== "status") {
      filtered = filtered.filter((p) => p.status === filters.status);
    }
    if (filters.saleDate && key !== "saleDate") {
      filtered = filtered.filter((p) => {
        const propDate = new Date(p.sale_date).toISOString().split("T")[0];
        const filterDate = new Date(filters.saleDate).toISOString().split("T")[0];
        return propDate === filterDate;
      });
    }
  
    return [...new Set(filtered.map((p) => p[key]))].sort();
  };
  
  const upsertUserPropertyData = async (newData = {}) => {
    try {
      await fetch(`https://api.deliquentauction.com/properties/property/user-data/?search_address=${encodeURIComponent(selectedProperty.search_address)}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          search_address: selectedProperty.search_address,
          notes,
          tags: selectedTags,
          saved: selectedProperty.saved,
          phone_number,
          email,
          ...newData,
        }),
      });
    } catch (error) {
      console.error("Error updating user property data:", error);
    }
  };
  
  const saveContactInfo = async () => {
    try {
      await upsertUserPropertyData({ phone_number, email });

      setSelectedProperty((prev) => ({
        ...prev,
        phone_number: phone_number,
        email: email,
      }));

      setIsEditing(false);
    } catch (error) {
      console.error("Error saving contact info:", error);
    }
  };  
  
  const openDetails = async (property) => {
    try {
      const propertyResponse = await fetch(
        `https://api.deliquentauction.com/properties/property/?search_address=${encodeURIComponent(property.search_address)}`,
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        }
      );
  
      if (!propertyResponse.ok) {
        throw new Error("Failed to fetch property data");
      }
  
      const fetchedProperty = await propertyResponse.json();
  
      const userDataResponse = await fetch(
        `https://api.deliquentauction.com/properties/property/user-data/?search_address=${encodeURIComponent(property.search_address)}`,
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        }
      );
  
      if (!userDataResponse.ok) {
        throw new Error("Failed to fetch user-specific data");
      }
  
      const userData = await userDataResponse.json();
  
      const updatedProperty = {
        ...fetchedProperty,
        notes: userData.notes || "",
        tags: userData.tags || [],
        saved: userData.saved || false,
        phone_number: userData.phone_number || "",
        email: userData.email || "",
      };
  
      setSelectedProperty(updatedProperty);
      setSelectedTags(updatedProperty.tags || []);
      setNotes(updatedProperty.notes || "");
      setSaved(updatedProperty.saved || false);
      setPhone(updatedProperty.phone_number || "");
      setEmail(updatedProperty.email || "");
  
      setDetailsOpen(true);
    } catch (error) {
      console.error("Error fetching property details:", error);
    }
  };
  
  const closeDetails = (property) => {
    setDetailsOpen(false);
    setSelectedProperty(null);
  };

  const customIcon = new L.Icon({
    iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
  });

  const saveNotes = async () => {
    try {
      await upsertUserPropertyData();
      setNoteSavedMessage("Note Saved!");
      setNoteErrorMessage("");
      setTimeout(() => setNoteSavedMessage(""), 2000);
    } catch (error) {
      console.error("Error saving notes", error);
      setNoteErrorMessage("Failed to save the note.");
      setTimeout(() => setNoteErrorMessage(""), 4000);
    }
  };

  const toggleSaveProperty = async () => {
    const isSaved = !selectedProperty.saved;
  
    setSelectedProperty((prev) => ({
      ...prev,
      saved: isSaved,
    }));
  
    await upsertUserPropertyData({ saved: isSaved });
  };
  
  const addTag = (tag) => {
    if (!selectedTags.includes(tag)) {
      const updatedTags = [...selectedTags, tag];
      setSelectedTags(updatedTags);
      setProperties((prevProperties) =>
        prevProperties.map((prop) =>
          prop.search_address === selectedProperty.search_address
            ? { ...prop, tags: updatedTags }
            : prop
        )
      );
      upsertUserPropertyData({ tags: updatedTags });
    }
  };

  const RemoveTag = (tag) => {
    const updatedTags = selectedTags.filter((t) => t !== tag);
    setSelectedTags(updatedTags);
    setProperties((prevProperties) =>
      prevProperties.map((prop) =>
        prop.search_address === selectedProperty.search_address
          ? { ...prop, tags: updatedTags }
          : prop
      )
    );
    upsertUserPropertyData({ tags: updatedTags });
  };

  const newCustomTag = (e) => {
    e.preventDefault();
    if (customTag.trim() !== "") {
      addTag(customTag.trim());
      setCustomTag("");
    }
  };

    return (
    <><div className="leads">
        <Header />

        <div className="filters">
          <select value={filters.address} onChange={(e) => setFilters({ ...filters, address: e.target.value })}>
            <option value="">Select Address</option>
            {getFilteredOptions("full_address").map((address) => (
              <option key={address} value={address}>
                {address}
              </option>
            ))}
          </select>
          <select value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })}>
            <option value="">Select City</option>
            {getFilteredOptions("parcel_city").map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
          <select value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })}>
            <option value="">Select State</option>
            {getFilteredOptions("state").map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
          <select value={filters.county} onChange={(e) => setFilters({ ...filters, county: e.target.value })}>
            <option value="">Select County</option>
            {getFilteredOptions("county").map((county) => (
              <option key={county} value={county}>
                {county}
              </option>
            ))}
          </select>
          <select value={filters.industry} onChange={(e) => setFilters({ ...filters, industry: e.target.value })}>
            <option value="">Select Distress List</option>
            {getFilteredOptions("industry").map((industry) => (
              <option key={industry} value={industry}>
                {industry}
              </option>
            ))}
          </select>
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="">Select Status</option>
            {getFilteredOptions("status").map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
          <select value={filters.saleDate} onChange={(e) => setFilters({ ...filters, saleDate: e.target.value })}>
            <option value="">Select Auction Date</option>
            {getFilteredOptions("sale_date").map((saleDate) => {
              const formattedDate = new Date(saleDate).toLocaleDateString("en-US", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit"
              });

              return (
                <option key={saleDate} value={saleDate}>
                  {formattedDate}
                </option>
              );
            })}
          </select>
          <button className="clear-filters-button" onClick={clearAllFilters}>Clear Filters</button>
        </div>

        <div className="active-filters">
          <div className="filters-list">
            {activeFilters.length > 0 ? (
              activeFilters.map((filter) => (
                <div key={filter} className="filter-chip">
                  <span>
                    {filter}: {filter === "saleDate"
                      ? new Date(filters[filter] + "T00:00:00Z").toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                      })
                      : filters[filter]}
                  </span>
                  <button onClick={() => removeFilter(filter)}>&times;</button>
                </div>
              ))
            ) : (
              <span className="no-filters">No filters selected</span>
            )}
          </div>
        </div>
        <div className="properties">
          {filteredProperties.map((property) => (
            <Card key={property.search_address} onClick={() => openDetails(property)}>
              <CardContent>

                <div className="card-header">
                  <h2 className="address">{property.full_address}</h2>
                  <span className={`status-badge ${property.status?.toLowerCase()}`}>{property.status}</span>
                </div>

                <div className="card-body">
                  <div className="card-detail">
                    <h3>Auction Date</h3>
                    <p>{new Date(property.sale_date).toLocaleDateString()}</p>
                  </div>

                  <div className="card-detail">
                    <h3>Opening Bid</h3>
                    <p>{property.opening_bid.toLowerCase() === "nan" ? "$0" : `${property.opening_bid}`}</p>
                  </div>

                  <div className="card-detail">
                    <h3>Parcel Value</h3>
                    <p>{property.parcel_value.toLowerCase() === "nan" ? "$0" : `${property.parcel_value}`}</p>
                  </div>

                  <div className="card-detail">
                    <h3>Distress List</h3>
                    <p>{property.industry}</p>
                  </div>

                  <div className="card-detail">
                    <h3>County</h3>
                    <p>{property.county}</p>
                  </div>

                  <div className="card-detail">
                    <h3>Parcel ID</h3>
                    <p>{property.parcel_id}</p>
                  </div>
                </div>

                <div className="card-footer"></div>
              </CardContent>
            </Card>
          ))}
        </div>

        <CardDetails isOpen={isDetailsOpen} onClose={closeDetails}>
          {selectedProperty && (
            <CardContent>

              <div className="card-header-max">
                <h2>{selectedProperty.full_address}</h2>
                <span>
                  <button
                    onClick={toggleSaveProperty}
                    className={`save-property-button ${selectedProperty.saved ? 'saved' : 'unsaved'}`}
                  >
                    {selectedProperty.saved ? 'Unsave' : 'Save'} Property
                  </button>
                </span>
                <span className={`status-badge ${selectedProperty.status?.toLowerCase()}`}>{selectedProperty.status}</span>
              </div>

              <div className="max-card-body">

                <details className="tag-details">
                  <summary>Tags</summary>

                  <div className="details-container">

                    <div className="max-card-detail">
                      <h3>Manage Tags</h3>
                      {default_tags.map((tag) => (
                        <button
                          key={tag}
                          onClick={() => {
                            selectedTags.includes(tag) ? RemoveTag(tag) : addTag(tag);
                          } }
                          className={selectedTags.includes(tag) ? "active" : ""}
                        >
                          {selectedTags.includes(tag) ? `${tag}` : tag}
                        </button>
                      ))}
                      <form onSubmit={newCustomTag}>
                        <input
                          type="text"
                          value={customTag}
                          onChange={(e) => setCustomTag(e.target.value)}
                          placeholder="Enter custom tag" />
                        <button type="submit">Add</button>
                      </form>
                    </div>
                    <div className="max-card-detail">
                      <h3>Current Tags</h3>
                      {Array.isArray(selectedTags) && selectedTags.length > 0 ? (
                        selectedTags.map((tag, index) => (
                          <span key={index} className="tag-item">
                            {tag}
                            <button onClick={() => RemoveTag(tag)}>✖</button>
                          </span>
                        ))
                      ) : (
                        <span>No Tags</span>
                      )}
                    </div>
                  </div>
                </details>

                <details className="auction-details" open>
                  <summary>Auction Information</summary>

                  <div className="details-container">

                    <div className="max-card-detail">
                      <h3>Auction Date</h3>
                      <p>{new Date(selectedProperty.sale_date).toLocaleDateString()}</p>
                    </div>

                    <div className="max-card-detail">
                      <h3>Opening Bid</h3>
                      <p>{selectedProperty.opening_bid && selectedProperty.opening_bid.toLowerCase() === "nan" ? "$0" : selectedProperty.opening_bid}</p>
                    </div>

                    <div className="max-card-detail">
                      <h3>Parcel Value</h3>
                      <p>{selectedProperty.parcel_value.toLowerCase() === "nan" ? "$0" : `${selectedProperty.parcel_value}`}</p>
                    </div>

                    <div className="max-card-detail">
                      <h3>Judgment</h3>
                      <p>{selectedProperty.judgment.toLowerCase() === "nan" ? "$0" : `${selectedProperty.judgment}`}</p>
                    </div>

                    <div className="max-card-detail">
                      <h3>Distress List</h3>
                      <p>{selectedProperty.industry}</p>
                    </div>
                  </div>
                </details>

                <details className="parcel-details" open>
                  <summary>Parcel Information</summary>

                  <div className="details-container">

                    <div className="max-card-detail">
                      <h3>County</h3>
                      <p>{selectedProperty.county}</p>
                    </div>

                    <div className="max-card-detail">
                      <h3>City</h3>
                      <p>{selectedProperty.parcel_city}</p>
                    </div>

                    <div className="max-card-detail">
                      <h3>Parcel ID</h3>
                      <p>{selectedProperty.parcel_id}</p>
                    </div>

                    <div className="max-card-detail">
                      <h3>Case Number</h3>
                      <p>{selectedProperty.case_number}</p>
                    </div>

                    <div className="max-card-detail">
                      <h3>Measurements</h3>
                      <p>{selectedProperty.measurements}</p>
                    </div>

                    <div className="max-card-detail">
                      <h3>Zoning Type</h3>
                      <p>{selectedProperty.zoning_type && selectedProperty.zoning_type.toLowerCase() === "nan" ? "N/A" : `${selectedProperty.zoning_type}`}</p>
                    </div>
                  </div>

                  <div className="search-address">
                    <h3>Search Address</h3>
                    <p>{selectedProperty.search_address}</p>
                  </div>
                </details>

                <details className="owner-details" open>
                  <summary>Owner Information</summary>

                  <div className="details-container">

                    <div className="max-card-detail">
                      <h3>Owner</h3>
                      <p>{selectedProperty.owner}</p>
                    </div>

                    <div className="max-card-detail">
                      <h3>Mailing Address</h3>
                      <p>{selectedProperty.mailing_address}</p>
                    </div>

                    <div className="max-card-detail">
                      <h3>Phone</h3>
                      {isEditing ? (
                        <input
                          type="tel"
                          value={phone_number}
                          placeholder="Enter phone number here..."
                          onChange={(e) => setPhone(e.target.value)} />
                      ) : (
                        <p>{selectedProperty?.phone_number}</p>
                      )}
                      <button onClick={() => setIsEditing((prev) => !prev)}>
                        {isEditing ? "Cancel" : "Edit Contact"}
                      </button>
                      {isEditing && (
                        <button onClick={saveContactInfo}>Save</button>
                      )}
                    </div>

                    <div className="max-card-detail">
                      <h3>Email</h3>
                      {isEditing ? (
                        <input
                          type="email"
                          value={email}
                          placeholder="Enter email address here..."
                          onChange={(e) => setEmail(e.target.value)} />
                      ) : (
                        <p>{selectedProperty?.email}</p>
                      )}
                    </div>
                  </div>
                </details>

                <details className="notes">
                  <summary>Notes</summary>

                  <div className="details-container">

                    <div className="max-card-detail">
                      <h3>Notes</h3>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Add notes here..." />
                      <button onClick={saveNotes}>Save Notes</button>
                      {noteSavedMessage && (
                        <p className="note-saved-message">{noteSavedMessage}</p>
                      )}
                      {noteErrorMessage && (
                        <p className="note-error-message">{noteErrorMessage}</p>
                      )}
                    </div>
                  </div>
                </details>
              </div>

              <details className="map-details">
                <summary>Map</summary>

                <div className="details-container">

                  <div className="map">
                    {selectedProperty.latitude && selectedProperty.longitude && (
                      <div className="map-container">
                        <MapContainer
                          center={[selectedProperty.latitude, selectedProperty.longitude]}
                          zoom={15}
                          style={{ height: "300px", width: "100%", borderRadius: "10px" }}
                        >
                          <TileLayer
                            url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}" />
                          <Marker position={[selectedProperty.latitude, selectedProperty.longitude]} icon={customIcon}>
                            <Popup>{selectedProperty.full_address}</Popup>
                          </Marker>
                        </MapContainer>
                      </div>
                    )}
                  </div>
                </div>
              </details>
            </CardContent>
          )}
        </CardDetails>
        <Footer />
      </div>
      </>
    
  );
}